import {
    Component,
    ComponentFactory,
    ComponentFactoryResolver,
    ComponentRef,
    ElementRef,
    inject,
    Injector,
    Input,
    OnChanges,
    OnInit,
    Renderer2,
    SimpleChanges,
    Type,
    ViewChild,
    ViewContainerRef
} from '@angular/core';
import { take, takeUntil } from 'rxjs/operators';
import { BaseContainer } from '../../../../core/common/containers/base.abstract.container';

import { FieldType, getParentDottedKey } from '../../../../core/field-definitions/entities/field-definition.entity';
import { ExtendedBaseProfileField } from '../../../../core/profiles/entities/extended-profile-field.entity';
import { AIProfileField } from '../../../../core/profiles/entities/profile-field.entity';
import { Profile } from '../../../../core/profiles/entities/profile.entity';
import { PartialRecord } from '../../../../core/records/entities/partial-record.class';
import { ShInjectionPointDirective } from '../../../directives/injection-point.directive';
import { DefaultReadonlyMapping } from '../../../entities/field-mapping/field-mapping.entity';
import { ShProfileConfigService } from '../../../services/profile-config.service';
import { ShBaseGenericReadonlyFieldComponent } from '../base-generic-readonly-field/base-generic-readonly-field.component';
import { ShUnsupportedReadonlyFieldComponent } from '../simple-readonly-fields/unsupported-readonly-field/unsupported-readonly-field.component';

export type GenericReadonlyFieldDescriptor = {
    type: Type<ShBaseGenericReadonlyFieldComponent>;
    config?: Record<string, unknown>;
};

@Component({
    selector: 'sh-generic-readonly-field',
    template: `<ng-template injection-point></ng-template>`,
    styleUrls: ['../base-generic-readonly-field/base-generic-readonly-field.component.scss']
})
export class ShGenericReadonlyFieldComponent extends BaseContainer implements OnInit, OnChanges {
    private componentRef!: ComponentRef<ShBaseGenericReadonlyFieldComponent>;

    @Input() profileField!: ExtendedBaseProfileField;

    @Input() aiProfile?: Profile;

    @Input() record!: PartialRecord;

    @Input() nestedIndex: number = 0;

    @ViewChild(ShInjectionPointDirective, { static: true }) adHost!: ShInjectionPointDirective;

    private readonly el: ElementRef = inject(ElementRef);
    private readonly renderer: Renderer2 = inject(Renderer2);

    constructor(
        private readonly componentFactoryResolver: ComponentFactoryResolver,
        private readonly profileConfigService: ShProfileConfigService
    ) {
        super();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.record && !changes.record.firstChange) {
            this.setPartialRecord();
        }

        if (changes.profileField && !changes.profileField.firstChange) {
            this.setProfileField();
        }
    }

    ngOnInit(): void {
        this.profileConfigService
            .getReadonlyOverrides()
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe(overrides => this.initChildComponent(overrides));

        this.initIconConditions();
    }

    private initChildComponent(overrides: Record<string, GenericReadonlyFieldDescriptor>): void {
        const defaultMapping = this.profileConfigService.getDefaultReadOnlyMappings() ?? DefaultReadonlyMapping;

        const dottedKey = this.profileField.FieldDefinition.DottedKey;
        const fieldType = this.profileField.FieldDefinition.Type;
        const controlOverride = this.profileField.Control;

        const { componentType, componentConfig } = this.resolveReadonlyComponent(
            overrides,
            dottedKey,
            fieldType,
            controlOverride,
            defaultMapping
        );

        const typeToLoad = componentType ?? ShUnsupportedReadonlyFieldComponent;
        if ((typeToLoad as typeof ShBaseGenericReadonlyFieldComponent).IS_COMPLEX) {
            this.renderer.addClass(this.el.nativeElement, 'sh-complex-field');
        }

        return this.loadChildComponent(typeToLoad, componentConfig);
    }

    private resolveReadonlyComponent(
        overrides: Record<string, GenericReadonlyFieldDescriptor>,
        dottedKey: string,
        fieldType: FieldType,
        controlOverride: string | undefined,
        defaultMapping: Record<FieldType, Type<ShBaseGenericReadonlyFieldComponent>>
    ): { componentType?: Type<ShBaseGenericReadonlyFieldComponent>; componentConfig: Record<string, any> } {
        if (controlOverride) {
            const parsed = this.parseControlOverride(controlOverride);
            if (typeof parsed === 'string') {
                return {
                    componentType:
                        this.profileConfigService.overrideReadonlyMapping[parsed] ?? defaultMapping[fieldType],
                    componentConfig: {}
                };
            }
            if (
                parsed &&
                Object.prototype.hasOwnProperty.call(parsed, 'type') &&
                Object.prototype.hasOwnProperty.call(parsed, 'config')
            ) {
                return {
                    componentType:
                        this.profileConfigService.overrideReadonlyMapping[parsed.type] ?? defaultMapping[fieldType],
                    componentConfig: parsed.config ?? {}
                };
            }
        }

        const override = overrides?.[dottedKey];
        return {
            componentType: override?.type ?? defaultMapping[fieldType],
            componentConfig: override?.config ?? {}
        };
    }

    private parseControlOverride(controlOverride: string): any {
        try {
            return JSON.parse(controlOverride);
        } catch {
            return controlOverride;
        }
    }

    private loadChildComponent(
        componentType: Type<ShBaseGenericReadonlyFieldComponent>,
        config: Record<string, unknown>
    ): void {
        const injector: Injector = Injector.create({
            providers: [
                {
                    provide: 'config',
                    useValue: config
                }
            ]
        });

        const componentFactory: ComponentFactory<any> =
            this.componentFactoryResolver.resolveComponentFactory(componentType);

        const viewContainerRef: ViewContainerRef = this.adHost.viewContainerRef;
        viewContainerRef.clear();

        this.componentRef = viewContainerRef.createComponent(componentFactory, undefined, injector);

        this.setPartialRecord();
        this.setProfileField();
        this.setNestedIndex();
    }

    private setPartialRecord(): void {
        if (this.componentRef?.instance) {
            this.componentRef.instance.record = this.record;
        }
    }

    private setProfileField(): void {
        if (this.componentRef?.instance) {
            this.componentRef.instance.profileField = this.profileField;
        }
    }

    private setNestedIndex(): void {
        if (this.componentRef?.instance) {
            this.componentRef.instance.nestedIndex = this.nestedIndex;
        }
    }

    // #region AI indicator
    private initIconConditions() {
        if (this.aiProfile?.Fields.find(aiField => this.matchingAIEnhancedField(aiField as AIProfileField))) {
            this.componentRef.instance.aiActive = true;
        }
    }

    private matchingAIEnhancedField(aiProfileField: AIProfileField): boolean {
        const isAIEnhancedField: boolean = aiProfileField.GenerativeMetadata.Hint !== null;
        const isMatchingField: boolean =
            aiProfileField.DottedKey === this.profileField.DottedKey ||
            getParentDottedKey(aiProfileField.DottedKey) === this.profileField.DottedKey; // Needed for listfields

        return isAIEnhancedField && isMatchingField;
    }
    // #endregion
}
